import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { keyframes, useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { itemImageUrl } from '../../api/client';
import type { LibraryItem, PlaybackProgress } from '../../api/types';
import { barOffset, easing, groundRgb, shellStart } from '../../theme';
import { episodeCode, formatDuration, formatRemaining } from './playbackTime';
import { ResumeProgress } from './WatchProgress';

// The still drifts, slowly enough that it reads as light moving rather than as the picture.
const drift = keyframes`
  from { transform: scale(1.04) translate3d(0, 0, 0); }
  to { transform: scale(1.12) translate3d(-1.5%, -1%, 0); }
`;

// No blur in it, though the website's sections have one: Safari on a phone keeps the last frame
// of a filter it animated, and the title stayed out of focus until the page was scrolled.
const rise = keyframes`
  from { opacity: 0; transform: translateY(18px); }
`;

/// The top of the home screen, the way a television shows it: one title across the whole width,
/// its still behind it and its name in its own lettering, with the way back into it underneath.
/// Nothing is shown without a still - a spotlight on an empty frame is a grey box.
export default function Spotlight({
  item,
  progress,
  eyebrow,
}: {
  item: LibraryItem;
  progress?: PlaybackProgress;
  eyebrow: string;
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const dark = theme.palette.mode === 'dark';
  const ground = dark ? groundRgb.dark : groundRgb.light;

  const backdrop = itemImageUrl(item, 'Backdrop');
  const logo = itemImageUrl(item, 'Logo');

  if (!backdrop) {
    return null;
  }

  // The wash runs from the side the title stands on, which is the side the writing starts from.
  const fromTitle = theme.direction === 'rtl' ? 270 : 90;
  const title = item.kind === 'Episode' && item.seriesTitle ? item.seriesTitle : item.title;
  const started = progress && progress.fraction > 0 && !progress.isWatched;

  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        isolation: 'isolate',
        // Out to the edges of the window - on under the glass of the sidebar and up under the
        // bar: the artwork is the page, not a picture on it. The copy stays in line with the
        // shelves below.
        marginInlineStart: { xs: -2, md: `calc(-32px - ${shellStart})` },
        marginInlineEnd: { xs: -2, md: -4 },
        mt: { xs: `calc(-16px - ${barOffset.xs}px)`, md: `calc(-24px - ${barOffset.md}px)` },
        minHeight: { xs: 440, md: 'min(76vh, 680px)' },
        display: 'flex',
        alignItems: 'flex-end',
        paddingInlineStart: { xs: 2, md: `calc(32px + ${shellStart})` },
        paddingInlineEnd: { xs: 2, md: 6 },
        pt: { xs: 14, md: 16 },
        pb: { xs: 4, md: 7 },
      }}
    >
      {/* The picture fades out into whatever is behind the page rather than being painted
          over with its colour - the ground carries a glow of its own, and a still that ended in
          plain black would show where it stops. */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          zIndex: -1,
          overflow: 'hidden',
          maskImage: 'linear-gradient(180deg, #000 0%, #000 50%, transparent 100%)',
          WebkitMaskImage: 'linear-gradient(180deg, #000 0%, #000 50%, transparent 100%)',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `url(${backdrop})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center 22%',
            animation: `${drift} 32s ease-in-out infinite alternate`,
            willChange: 'transform',
          }}
        />

        {/* A wash from the side the title stands on, so it stays readable whatever the still
            is. It starts where the content does: under the sidebar the picture stays whole, for
            the sidebar's glass to blur. And a breath from above for the bar. */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: {
              xs:
                `linear-gradient(${fromTitle}deg, rgba(${ground},0.94) 0%, rgba(${ground},0.6) 38%, rgba(${ground},0) 72%),`
                + ` linear-gradient(0deg, rgba(${ground},0.7) 0%, rgba(${ground},0) 45%),`
                + ` linear-gradient(180deg, rgba(${ground},0.55) 0%, rgba(${ground},0) 22%)`,
              md:
                `linear-gradient(${fromTitle}deg, rgba(${ground},0.35) 0px, rgba(${ground},0.94) ${shellStart},`
                + ` rgba(${ground},0.6) calc(${shellStart} + 30%), rgba(${ground},0) calc(${shellStart} + 60%)),`
                + ` linear-gradient(180deg, rgba(${ground},0.5) 0%, rgba(${ground},0) 22%)`,
            },
          }}
        />
      </Box>

      <Stack
        spacing={2}
        sx={{
          width: '100%',
          maxWidth: 620,
          '& > *': { animation: `${rise} 1s ${easing.out} both` },
          '& > :nth-of-type(2)': { animationDelay: '80ms' },
          '& > :nth-of-type(3)': { animationDelay: '160ms' },
          '& > :nth-of-type(4)': { animationDelay: '240ms' },
          '& > :nth-of-type(5)': { animationDelay: '320ms' },
        }}
      >
        <Typography variant="overline" sx={{ color: 'primary.light', lineHeight: 1 }}>
          {eyebrow}
        </Typography>

        {logo ? (
          <Box
            component="img"
            src={logo}
            // The picture is the title, so it is read out as the title.
            alt={title}
            sx={{
              alignSelf: 'flex-start',
              maxWidth: { xs: 240, sm: 340, md: 440 },
              maxHeight: { xs: 90, md: 150 },
              objectFit: 'contain',
              objectPosition: theme.direction === 'rtl' ? 'right bottom' : 'left bottom',
              display: 'block',
              filter: dark
                ? 'drop-shadow(0 2px 24px rgba(0,0,0,0.6))'
                : 'drop-shadow(0 1px 2px rgba(0,0,0,0.55)) drop-shadow(0 2px 18px rgba(0,0,0,0.35))',
            }}
          />
        ) : (
          <Typography
            variant="h1"
            component="h2"
            sx={{
              fontSize: { xs: '2.5rem', md: '4rem' },
              letterSpacing: '-0.045em',
              lineHeight: 0.95,
              textShadow: `0 2px 24px rgba(${dark ? '0,0,0' : '255,255,255'},${dark ? 0.6 : 0.85})`,
            }}
          >
            {title}
          </Typography>
        )}

        <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap', alignItems: 'center' }}>
          {item.kind === 'Episode' && item.seasonNumber !== null && item.episodeNumber !== null && (
            <Chip size="small" color="primary" label={episodeCode(item.seasonNumber, item.episodeNumber)} />
          )}
          {item.year && <Chip size="small" variant="outlined" label={item.year} />}
          {item.duration && <Chip size="small" variant="outlined" label={formatDuration(item.duration)} />}
          {item.officialRating && <Chip size="small" variant="outlined" label={item.officialRating} />}
          {item.genres.slice(0, 2).map((genre) => (
            <Chip key={genre} size="small" variant="outlined" label={genre} />
          ))}
        </Stack>

        {item.overview && (
          <Typography
            sx={{
              color: 'text.secondary',
              fontSize: { xs: '0.9375rem', md: '1.0625rem' },
              lineHeight: 1.5,
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {item.overview}
          </Typography>
        )}

        <Stack direction="row" spacing={2.5} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap', pt: 1 }}>
          <Button
            variant="contained"
            size="large"
            endIcon={<ArrowForwardIcon sx={{ transform: theme.direction === 'rtl' ? 'scaleX(-1)' : undefined }} />}
            onClick={() => navigate(`/items/${item.id}`)}
          >
            {t('dashboard.spotlight.open')}
          </Button>

          {started && progress && (
            <Stack spacing={0.75} sx={{ minWidth: 160, flexGrow: { xs: 1, sm: 0 } }}>
              <ResumeProgress fraction={progress.fraction} />
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                {t('item.remaining', { time: formatRemaining(progress.position, progress.duration ?? item.duration) })}
              </Typography>
            </Stack>
          )}
        </Stack>
      </Stack>
    </Box>
  );
}
